'use client';

import type { TagData } from 'src/types/tag-data';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@savvy-kit/components/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@savvy-kit/components/dropdown-menu';
import Tag from '@savvy-kit/patterns/tag';
import { isNil } from 'es-toolkit';
import { ChevronDown } from 'lucide-react';
import { tagIndicatorPropsFromTagData } from 'src/utils/advisor-dash/tag-indicator-from-tag-data';

import { TagsFilterContent } from '../../common/filters/tags-filter-content';

type ActiveTagsFilterDropdownProps = {
  readonly allTags: readonly TagData[];
  readonly selectedTagIds: readonly string[];
  readonly onToggleTag: (tagId: string) => void;
  readonly onClearAll: () => void;
  readonly visibleTags: readonly TagData[];
  readonly remainingCount: number;
  readonly hasMoreTags: boolean;
  readonly onRemoveTag: (tagId: string) => void;
  readonly filterLabel: string;
};

export function ActiveTagsFilterDropdown({
  allTags,
  selectedTagIds,
  onToggleTag,
  onClearAll,
  visibleTags,
  remainingCount,
  hasMoreTags,
  onRemoveTag,
  filterLabel,
}: ActiveTagsFilterDropdownProps) {
  const [search, setSearch] = useState('');
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(undefined);
  const [alignOffset, setAlignOffset] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (containerRef.current && triggerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();
      setDropdownWidth(containerRect.width);
      setAlignOffset(containerRect.left - triggerRect.left);
    }
  }, [hasMoreTags, visibleTags.length]);

  return (
    <div
      ref={containerRef}
      className="flex items-center flex-row flex-wrap border border-border-light rounded-md gap-2 px-2 py-2"
    >
      <span className="text-dark font-medium normal-case text-sm not-italic border-r border-border-light pr-2 mr-2">
        {filterLabel}
      </span>
      {visibleTags.map((tag) => (
        <Tag
          key={tag.id}
          {...tagIndicatorPropsFromTagData(tag)}
          type="filter"
          dismissible
          onDismiss={() => {
            onRemoveTag(tag.id);
          }}
        >
          {tag.name}
        </Tag>
      ))}
      {hasMoreTags && (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger
            render={
              <button
                ref={triggerRef}
                type="button"
                className="flex items-center gap-1 cursor-pointer border-none bg-transparent p-0 hover:opacity-70"
                aria-label="Show more tags"
              />
            }
          >
            <Badge variant="outline" className="shrink-0">
              +{remainingCount}
            </Badge>
            <ChevronDown className="size-4 text-dark shrink-0" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="p-0 overflow-x-hidden"
            align="start"
            sideOffset={10}
            alignOffset={alignOffset}
            style={!isNil(dropdownWidth) ? { width: dropdownWidth } : undefined}
          >
            <TagsFilterContent
              allTags={allTags}
              selectedTagIds={selectedTagIds}
              onToggleTag={onToggleTag}
              onClearAll={onClearAll}
              search={search}
              onSearchChange={setSearch}
              searchInputRef={searchInputRef}
              onEnterKeyDown={() => {
                setIsOpen(false);
              }}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
